import React, { Ref, useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, Clock, CurrencyDollar, MarkerPin01, Phone } from "@untitled-ui/icons-react";
import { Card, Collapsible } from "@myhealth-raccoon/components";

import { GoogleMap } from "./GoogleMap";
import { CopyTextButton } from "./CopyTextButton";
import moment from "moment";
import classNames from "classnames";
import { useClinicSelector } from "src/hooks/useAsyncSelector";
import { UmbracoClinicModel } from "src/services/booking";
import { clinicName } from "src/utils/name";
import { innerHtml } from "src/utils/html";
import { AutomedDetails } from "src/services/site";

const now = moment();

const allDaysOfWeek = Array(7).fill(0)
    .map((_, i) => {
        const weekday = moment(now).set("isoWeekday", i + 1);
        if (weekday.isBefore(now, "date")) {
            weekday.add(1, "week");
        }
        return weekday;
    });

type Props = {
    hideMap?: boolean
    hideTitle?: boolean
    onClickCancellationPolicy?(): void;
    onLoad?(clinic: UmbracoClinicModel & AutomedDetails): void;
}

export function ClinicInfoCard({ hideMap, hideTitle, onClickCancellationPolicy, onLoad }: Props) {
    const { data: clinic } = useClinicSelector();
    useEffect(() => {
        if (clinic && onLoad) {
            onLoad(clinic)
        }
    }, [onLoad, clinic]);

    const { address, latitude: lat, longitude: long, phoneNumber: tel, openingHours, specialHours: specialOpeningHours } = clinic || {};

    const addressRef = useRef<HTMLElement>(null);
    const telRef = useRef<HTMLElement>(null);
    const [openingHoursOpen, setOpeningHoursOpen] = useState(false);

    const adjustedOpeningHours = useMemo(() => {
        const spOp = specialOpeningHours?.map(({ date, title, openingTime, closingTime }) => {
            const day = moment(date);
            const open = moment(openingTime).set({
                year: day.year(),
                month: day.month(),
                date: day.date(),
            })
            const close = moment(closingTime).set({
                year: day.year(),
                month: day.month(),
                date: day.date(),
            })
            return { dayName: title, openingTime: open, closingTime: close }
        }) ?? [];
        return openingHours?.map(oh => {
            const openingTime = moment(oh.openingTime);
            const closingTime = moment(oh.closingTime);

            const date = moment(now).isoWeekday(oh.dayOfWeek);
            if (date.isBefore(now, "date")) {
                date.add(1, "week");
            }

            const specialDay = spOp.find(d => d.openingTime.isSame(date, "date"));
            if (specialDay) {
                return specialDay;
            }

            openingTime.set({
                year: date.year(),
                month: date.month(),
                date: date.date()
            });

            closingTime.set({
                year: date.year(),
                month: date.month(),
                date: date.date()
            });
            return { openingTime, closingTime, dayName: date.format("dddd") };
        }).sort((a, b) => a.openingTime.valueOf() - b.openingTime.valueOf()) ?? [];
    }, [openingHours, specialOpeningHours]);

    const { currentDay, nextDay } = useMemo(() => {
        const currentDay = adjustedOpeningHours[0];

        let nextDay = adjustedOpeningHours[1];
        if (!currentDay || !nextDay) {
            return { currentDay: null, nextDay: null }
        }
        while (nextDay.openingTime.isSame(nextDay.closingTime, 'minute') && adjustedOpeningHours.indexOf(nextDay) < adjustedOpeningHours.length - 1) {
            nextDay = adjustedOpeningHours[adjustedOpeningHours.indexOf(nextDay) + 1]
        }
        let openingTime = currentDay.openingTime;
        if (openingTime.isBefore(now)) {
            openingTime = nextDay.openingTime;
        }

        return { currentDay, nextDay };
    }, [adjustedOpeningHours]);

    return <div className="text-gray-800 text-base font-medium">
        {!hideMap && lat && long && <div className="bg-gray-300 h-52">
            <GoogleMap lat={lat} long={long} label={clinicName(clinic)} />
        </div>}
        <Card.Body className="space-y-4">
            {!hideTitle && <h2 className="font-semibold text-lg text-gray-900">
                {clinicName(clinic)}
            </h2>}
            {address &&
                <div className="flex flex-row gap-2.5">
                    <MarkerPin01 className="w-6 h-6 text-gray-400" />
                    <div className="flex-1"
                        // href={`https://www.google.com/maps/dir/?api=1&destination=${lat}%2C${long}`}
                        ref={addressRef as Ref<HTMLDivElement>}
                        {...innerHtml(address)}
                    />
                    <div>
                        <CopyTextButton contentRef={addressRef} />
                    </div>
                </div>
            }
        </Card.Body>
        {tel && <>
            <hr />
            <Card.Body className="flex flex-row gap-2.5">
                <Phone className="w-6 h-6 text-gray-400" />
                <div className="flex-1 block" ref={telRef as Ref<HTMLDivElement>}>
                    {tel}
                </div>
                <div>
                    <CopyTextButton contentRef={telRef} />
                </div>
            </Card.Body>
        </>}

        {openingHours?.length && openingHours?.length > 0 && currentDay && nextDay &&
            (<>
                <hr />
                <Card.Body>
                    <Collapsible
                        hideButtonLabel
                        className="px-0 py-0"
                        open={openingHoursOpen} setOpen={setOpeningHoursOpen}
                        title={<div className="flex flex-row gap-2.5">
                            <Clock className="w-6 h-6 text-gray-400" />
                            <div className="font-medium text-[15px] text-gray-600 flex flex-row flex-wrap gap-x-1">
                                {
                                    now.isBefore(currentDay.closingTime) && now.isAfter(currentDay.openingTime)
                                        // currently open
                                        ? currentDay.closingTime.diff(now, "minutes") > 90
                                            // not closing soon
                                            ? <>
                                                <span className="font-semibold text-success-500">Open Now</span>
                                                <span>•Closes {currentDay.closingTime.format(currentDay.closingTime.minutes() === 0 ? "h A" : "LT")}</span>
                                            </>
                                            // closing soon
                                            : <>
                                                <span className="text-warning-500">Closes Soon</span>
                                                <span>•{currentDay.closingTime.format(currentDay.closingTime.minutes() === 0 ? "h A" : "LT")}</span>
                                                <span>•Opens {nextDay.openingTime.format(nextDay.openingTime.minutes() === 0 ? "h A ddd" : "LT ddd")}</span>
                                            </>
                                        // currently closed
                                        : <>
                                            <span className="text-error-500">Closed</span>
                                            <span>•Opens {(currentDay.openingTime.isAfter(now) ? currentDay : nextDay).openingTime.format("LT ddd")}</span>
                                        </>
                                }
                            </div>
                        </div>}>
                        <ol>
                            {allDaysOfWeek
                                .sort((a, b) => a.isoWeekday() - b.isoWeekday())
                                .map(d => {
                                    const hours = adjustedOpeningHours.find(a => a.openingTime.isSame(d, "date"))
                                    return <li className="flex flex-row justify-between" key={d.toString()}>
                                        <span className={classNames(d.isSame(now, "date") ? "font-semibold" : "font-medium")}>
                                            {hours?.dayName ?? d.format("dddd")}
                                        </span>
                                        <span className={classNames(d.isSame(now, "date") ? "font-semibold" : "font-normal")}>
                                            {hours && !hours.openingTime.isSame(hours.closingTime, "minute")
                                                ?
                                                `${hours.openingTime.format("LT")}
                                                -
                                                ${hours.closingTime.format("LT")}`
                                                : <span className="text-error-500">Closed</span>}
                                        </span>
                                    </li>
                                })}
                        </ol>
                    </Collapsible>
                </Card.Body>
            </>)
        }
        {onClickCancellationPolicy && <>
            <hr />
            <Card.Body as="button" className="flex flex-row gap-2.5" onClick={onClickCancellationPolicy}>
                <CurrencyDollar className="w-6 h-6 text-gray-400" />
                <div className="flex-1 block">
                    Pricing & Cancellation Info
                </div>
                <div>
                    <ChevronRight className="w-6 h-6 text-gray-400" />
                </div>
            </Card.Body>
        </>}
    </div >
}